import React, { useMemo, useState } from "react"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { CollapseElement } from "../../../elements/collapse.element"
import { BlueCardEligibleBadgeElement } from "../../../elements/coverageBadges/BlueCardEligibleBadge.element"
import { ActiveAtRiskPlanStatusBadgeElement } from "../../../elements/coverageBadges/activeAtRiskPlanStatusBadge.element"
import { CheckTypeBadgeElement } from "../../../elements/coverageBadges/checkTypeBadge.element"
import { LastCheckedBadgeElement } from "../../../elements/coverageBadges/lastCheckedBadge.element"
import { ModalityCodeBadgeElement } from "../../../elements/coverageBadges/modalityCodeBadge.element"
import { NetworkBadgeElement } from "../../../elements/coverageBadges/networkBadge.element"
import { PartialCoverageBadgeElement } from "../../../elements/coverageBadges/partialCoverageBadge.element"
import { PermissionsHelper } from "../../../helpers/permissions.helper"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import type { ICoverageResultBreakdownComponentProps } from "../../../types/props.types"
import { ReportCoverageCheckResultPopupComponent } from "../../popups/reportCoverageCheckResultPopup.component"
import { CoverageResultBreakdownComponent } from "./coverageResultBreakdown.component"

import flagRedIcon from "@front-shared/assets/images/icons/flag-red.svg"

export const CoverageResultBreakdownWrapperComponent: React.FunctionComponent<
    ICoverageResultBreakdownWrapperComponentProps
> = props => {
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [isReportingResults, setIsReportingResults] = useState<boolean>(false)

    const canReportCoverageCheckResult = useMemo<boolean>(
        () =>
            !props.ignoreReporting &&
            PermissionsHelper.hasPermission(
                OneVerifyPermission.reportCoverageCheck,
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    const policyRightHeaderContent = useMemo<JSX.Element>(() => {
        return (
            <div className="flex items-center relative gap-[8px] flex-wrap">
                <CheckTypeBadgeElement coverageResult={props.coverageResult} />

                <NetworkBadgeElement coverageResult={props.coverageResult} />

                <BlueCardEligibleBadgeElement
                    coverageResult={props.coverageResult}
                />

                <PartialCoverageBadgeElement
                    coverageResult={props.coverageResult}
                />

                <ActiveAtRiskPlanStatusBadgeElement
                    coverageResult={props.coverageResult}
                />

                <LastCheckedBadgeElement
                    coverageResult={props.coverageResult}
                />

                <ModalityCodeBadgeElement requestData={props.requestData} />

                {canReportCoverageCheckResult && (
                    <div
                        className="absolute z-10 right-0 cursor-pointer"
                        onClick={e => {
                            e.stopPropagation()
                            setIsReportingResults(true)
                        }}
                    >
                        <img src={flagRedIcon} alt="Report result" />
                    </div>
                )}
            </div>
        )
    }, [canReportCoverageCheckResult, props.coverageResult, props.requestData])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <CollapseElement
                className="p-[24px]"
                label="Policy"
                content={
                    <CoverageResultBreakdownComponent
                        coverageResult={props.coverageResult}
                        requestData={props.requestData}
                        coverageOverride={props.coverageOverride}
                        isSmartScanFloating={props.isSmartScanFloating}
                        smartScanRightMarginClassName={
                            props.smartScanRightMarginClassName
                        }
                        onSmartScanSuccess={props.onSmartScanSuccess}
                        requestPayer={props.requestPayer}
                        responsePayer={props.responsePayer}
                        requestedHealthProvider={props.requestedHealthProvider}
                        policy={props.policy}
                        isOverridesModeActivated={
                            props.isOverridesModeActivated
                        }
                        overridesFormKey={props.overridesFormKey}
                        isOverridesFormDisabled={props.isOverridesFormDisabled}
                        withFlagsSection={props.withFlagsSection}
                        agenticCheck={props.agenticCheck}
                        preAgenticCoverageResult={
                            props.preAgenticCoverageResult
                        }
                    />
                }
                rightHeaderContent={policyRightHeaderContent}
            />

            {isReportingResults && (
                <ReportCoverageCheckResultPopupComponent
                    isActive
                    coverageResult={props.coverageResult}
                    onDone={() => {
                        setIsReportingResults(false)
                    }}
                />
            )}
        </div>
    )
}

interface ICoverageResultBreakdownWrapperComponentProps
    extends ICoverageResultBreakdownComponentProps {
    ignoreReporting?: boolean
}
